import { useEffect, useId, useRef, type ReactNode } from "react";
import type { CampaignChannel, Channel } from "../api/types";
import { Mail, MessageCircle, Smartphone, BellRing, X } from "./icons";
import { t, tx } from "../i18n";

/* The CRM's few shared building blocks. Each screen composes these. Their own labels go through t() (src/i18n), so a screen built from them is translated without changes. */

export function PageHead({ eyebrow, title, sub, actions }: { eyebrow?: string; title: string; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="crm-head">
      <div>
        {eyebrow && <p className="eyebrow">{t(eyebrow)}</p>}
        <h1>{t(title)}</h1>
        {sub && <p className="crm-head__sub">{tx(sub)}</p>}
      </div>
      {actions && <div className="crm-head__actions">{actions}</div>}
    </header>
  );
}

export type Tone = "neutral" | "green" | "amber" | "red" | "gold" | "ink";

export function Pill({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`crm-pill is-${tone}`}>{tx(children)}</span>;
}

/** With onClick it is a button that filters what is below it; active marks the one in use. */
export function Stat({
  label,
  value,
  note,
  tone,
  onClick,
  active,
}: {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: "up" | "down";
  onClick?: () => void;
  active?: boolean;
}) {
  const body = (
    <>
      <span>{t(label)}</span>
      <strong className="num">{value}</strong>
      {note && <small className={tone === "up" ? "is-pos" : tone === "down" ? "is-neg" : undefined}>{note}</small>}
    </>
  );
  if (!onClick) return <div className="crm-stat">{body}</div>;
  return (
    <button type="button" className={"crm-stat is-button" + (active ? " is-active" : "")} onClick={onClick} aria-pressed={active}>
      {body}
    </button>
  );
}

export function Stats({ children }: { children: ReactNode }) {
  return <section className="crm-stats">{children}</section>;
}

/** Filters and ranges: a segmented pill. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: ReactNode; count?: number }[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="crm-seg" role="group" aria-label={t(label)}>
      {options.map((o) => (
        <button key={o.value} type="button" className={o.value === value ? "is-on" : ""} aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {tx(o.label)}
          {o.count !== undefined && <em className="num">{o.count}</em>}
        </button>
      ))}
    </div>
  );
}

/** Views within a page: underline tabs. */
export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { value: T; label: string; count?: number }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="crm-tabs" role="tablist">
      {tabs.map((tab) => (
        <button key={tab.value} role="tab" type="button" aria-selected={tab.value === value} className={tab.value === value ? "is-on" : ""} onClick={() => onChange(tab.value)}>
          {t(tab.label)}
          {tab.count !== undefined && <em className="num">{tab.count}</em>}
        </button>
      ))}
    </div>
  );
}

export function Loading({ what = "Loading…" }: { what?: string }) {
  return <p className="crm-state" aria-busy="true">{t(what)}</p>;
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <p className="crm-state is-error" role="alert">
      {message}
      {onRetry && (
        <button type="button" className="wo-link" onClick={onRetry}>
          {t("Try again")}
        </button>
      )}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="crm-state">{tx(children)}</p>;
}

/** Loading / error / empty in one place, so every list says the same thing the same way. */
export function Async<T>({
  state,
  empty,
  children,
}: {
  state: { data: T | null; error: string | null; loading: boolean; reload: () => void };
  empty?: (d: T) => ReactNode | false;
  children: (d: T) => ReactNode;
}) {
  if (state.error && !state.data) return <ErrorNote message={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading />;
  const e = empty?.(state.data);
  if (e) return <Empty>{e}</Empty>;
  return <>{children(state.data)}</>;
}

/* ---------- overlays ---------- */

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

function useFocusOnOpen<T extends HTMLElement>(open: boolean) {
  const ref = useRef<T>(null);
  const last = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    last.current = document.activeElement as HTMLElement | null;
    ref.current?.focus({ preventScroll: true });
    return () => last.current?.focus?.({ preventScroll: true });
  }, [open]);
  return ref;
}

/** A centred box for a record or a form. Full screen on a phone. */
export function Drawer({ open, title, sub, onClose, children, footer, wide }: { open: boolean; title: string; sub?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  useEscape(open, onClose);
  const ref = useFocusOnOpen<HTMLDivElement>(open);
  const id = useId();
  if (!open) return null;
  return (
    <div className="crm-scrim is-centre" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className={`crm-drawer${wide ? " is-wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby={id} ref={ref} tabIndex={-1}>
        <header className="crm-drawer__head">
          <div>
            <h2 id={id}>{t(title)}</h2>
            {sub && <p>{tx(sub)}</p>}
          </div>
          <button type="button" className="icon-button" aria-label={t("Close")} onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="crm-drawer__body">{children}</div>
        {footer && <footer className="crm-drawer__foot">{footer}</footer>}
      </aside>
    </div>
  );
}

/** A small centred question: a reason, a confirmation. */
export function Dialog({ open, title, onClose, children, footer }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer: ReactNode }) {
  useEscape(open, onClose);
  const ref = useFocusOnOpen<HTMLDivElement>(open);
  const id = useId();
  if (!open) return null;
  return (
    <div className="crm-scrim is-centre" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="crm-dialog" role="dialog" aria-modal="true" aria-labelledby={id} ref={ref} tabIndex={-1}>
        <h2 id={id}>{t(title)}</h2>
        <div className="crm-dialog__body">{children}</div>
        <footer className="crm-drawer__foot">{footer}</footer>
      </div>
    </div>
  );
}

/* ---------- forms ---------- */

export function Field({ label, hint, children, full }: { label: string; hint?: ReactNode; children: ReactNode; full?: boolean }) {
  return (
    <label className={`crm-field${full ? " is-full" : ""}`}>
      <span>{t(label)}</span>
      {children}
      {hint && <small>{tx(hint)}</small>}
    </label>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className="crm-form-error" role="alert">
      {message}
    </p>
  );
}

/** Key-value facts on a record. */
export function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="crm-facts">
      {items.map(([k, v], i) => (
        // by position too: a label can repeat (two unapplies of one note from the same invoice)
        <div key={`${i}:${k}`}>
          <dt>{t(k)}</dt>
          <dd>{v ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ---------- channels ---------- */

const CHANNEL: Record<Channel, { label: string; Icon: typeof Mail }> = {
  in_app: { label: "In-app", Icon: BellRing },
  email: { label: "Email", Icon: Mail },
  whatsapp: { label: "WhatsApp", Icon: MessageCircle },
  sms: { label: "SMS", Icon: Smartphone },
};

export const channelLabel = (c: Channel) => CHANNEL[c].label;

export function ChannelTag({ channel }: { channel: Channel | CampaignChannel }) {
  const { label, Icon } = CHANNEL[channel];
  return (
    <span className={`crm-chan is-${channel}`}>
      <Icon size={13} strokeWidth={1.8} />
      {label}
    </span>
  );
}

/** A thin horizontal meter, e.g. open rate or response rate. */
export function Meter({ value, max, tone = "ink" }: { value: number; max: number; tone?: "ink" | "gold" | "green" | "red" }) {
  const w = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <span className={`crm-meter is-${tone}`} aria-hidden="true">
      <i style={{ width: `${w}%` }} />
    </span>
  );
}
